import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faPhone,
  faChevronLeft,
  faChevronRight,
  faUser,
  faClock,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { convertUtcToIst } from "../../utilities.js";
import * as urls from "../../api/urlHelper";
import * as func from "../../api/apiHelper";

const PagBtn = ({ disabled, onClick, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-3 py-1 rounded-md text-sm transition ${
      disabled
        ? "text-gray-300 cursor-not-allowed"
        : "text-indigo-600 hover:bg-indigo-50"
    }`}
  >
    {children}
  </button>
);

const InfoRow = ({ label, value, className = "" }) => (
  <tr className="border-b last:border-0">
    <td className="py-2 px-3 font-medium text-gray-600 w-40">{label}</td>
    <td className={`py-2 px-3 text-gray-800 ${className}`}>{value ?? "N/A"}</td>
  </tr>
);

const ViewSubscriptionDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subId } = location.state;

  const limit = 5;
  const [subscription, setSubscription] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [renewalsMeta, setRenewalsMeta] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await func.get(
          `${urls.VIEW_SUBSCRIPTION_DETAILS_URL + subId}?page=${currentPage}&limit=${limit}`
        );
        if (response?.data?.success) {
          setSubscription(response?.data?.data?.subscription);
          setRenewals(response?.data?.data?.renewals);
          setRenewalsMeta(response?.data.data?.renewalsMeta);
        } else {
          toast.warn("Failed to fetch subscription details");
          navigate("/manage-subscription/list");
        }
      } catch (err) {
        toast.error("An error occurred while fetching data");
      }
      setLoading(false);
    };

    fetchData();
  }, [subId, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (renewalsMeta?.totalPages || 1)) {
      setCurrentPage(newPage);
    }
  };

  if (loading && !subscription) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm p-6 animate-pulse space-y-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faReceipt} className="text-white text-lg" />
            <h1 className="text-lg font-semibold text-white">Subscription Details</h1>
          </div>
        </div>

        {subscription && (
          <>
            {/* Plan Details */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Plan Details</h3>
              <table className="w-full text-sm">
                <tbody>
                  <InfoRow
                    label="Status"
                    value={
                      <span
                        className={`font-semibold ${
                          subscription.service_plan_status.includes("TERMINATED")
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {subscription.service_plan_status.replaceAll("_", " ")}
                      </span>
                    }
                  />
                  <InfoRow
                    label="Duration (Months)"
                    value={subscription?.service_plan_details?.["duration In(Months)"]}
                  />
                  <InfoRow
                    label="Start Date"
                    value={convertUtcToIst(subscription.service_start_date)}
                  />
                  <InfoRow
                    label="End Date"
                    value={convertUtcToIst(subscription.service_end_date)}
                  />
                  <InfoRow
                    label="Platform Split"
                    value={`₹${subscription.service_plan_details.platform_split}`}
                  />
                  <InfoRow
                    label="Discount"
                    value={`₹${subscription.service_plan_details.discount}`}
                  />
                  <InfoRow
                    label="GST"
                    value={`₹${subscription.service_plan_details.gst_amount}`}
                  />
                  <InfoRow
                    label="Total"
                    value={
                      <span className="font-semibold text-gray-900">
                        ₹{subscription.service_plan_details.total}
                      </span>
                    }
                  />
                </tbody>
              </table>
            </div>

            {/* Initial Payment */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Initial Payment</h3>
              <table className="w-full text-sm">
                <tbody>
                  <InfoRow
                    label="Transaction ID"
                    value={subscription?.paymentOrder?.merchant_transaction_id || "N/A"}
                  />
                  <InfoRow
                    label="Payment Date"
                    value={
                      subscription.paymentOrder?.payment_transaction_confirmation_time
                        ? convertUtcToIst(
                            subscription.paymentOrder.payment_transaction_confirmation_time
                          )
                        : "N/A"
                    }
                  />
                  <InfoRow
                    label="Amount"
                    value={`₹${subscription?.paymentOrder?.amount || 0}`}
                  />
                  <InfoRow
                    label="Status"
                    value={
                      <span
                        className={`font-semibold ${
                          subscription?.paymentOrder?.payment_status === "SUCCESS"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {subscription?.paymentOrder?.payment_status || "N/A"}
                      </span>
                    }
                  />
                </tbody>
              </table>
            </div>

            {/* Seeker + BG Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Matchseeker */}
              <div className="bg-white rounded-xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Matchseeker Details</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faUser} className="text-indigo-400 w-4" />
                    <span className="font-medium">Name:</span>
                    {subscription?.matchseekerName && subscription?.matchseekerId ? (
                      <Link
                        to={`/user-management/match-seekers/view/${subscription.matchseekerId}`}
                        className="text-indigo-600 underline hover:text-indigo-800"
                      >
                        {subscription.matchseekerName}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </p>
                  <p className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPhone} className="text-indigo-400 w-4" />
                    <span className="font-medium">Mobile:</span>
                    {subscription?.matchseekerMobile || "N/A"}
                  </p>
                </div>
              </div>

              {/* BG Profile */}
              <div className="bg-white rounded-xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">BG Profile Details</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faUser} className="text-indigo-400 w-4" />
                    <span className="font-medium">Name:</span>
                    {subscription?.bgFirstName && subscription?.bgId ? (
                      <Link
                        to={`/user-management/bride-groom-profile/view/${subscription.bgId}`}
                        className="text-indigo-600 underline hover:text-indigo-800"
                      >
                        {subscription.bgFirstName}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </p>
                  <p className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPhone} className="text-indigo-400 w-4" />
                    <span className="font-medium">Mobile:</span>
                    {subscription?.bgContact || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Renewals */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Renewals History</h3>

          {renewals.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                    <tr>
                      <th className="px-3 py-3">#</th>
                      <th className="px-3 py-3">Txn ID</th>
                      <th className="px-3 py-3">Renewed Date</th>
                      <th className="px-3 py-3">Duration (Months)</th>
                      <th className="px-3 py-3">Amount</th>
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renewals.map((r, index) => (
                      <tr key={r._id} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-3 py-3">{(currentPage - 1) * limit + index + 1}</td>
                        <td className="px-3 py-3">
                          {r.paymentOrder?.merchant_transaction_id || "N/A"}
                        </td>
                        <td className="px-3 py-3">
                          {r?.renewed_date ? convertUtcToIst(r?.renewed_date) : "N/A"}
                        </td>
                        <td className="px-3 py-3">
                          {r?.service_plan_details?.["duration In(Months)"] || "N/A"}
                        </td>
                        <td className="px-3 py-3 font-semibold text-gray-800">
                          ₹{r?.paymentOrder?.amount || 0}
                        </td>
                        <td className="px-3 py-3">
                          {r.paymentOrder?.payment_status === "SUCCESS" ? (
                            <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                              <FontAwesomeIcon icon={faCheckCircle} /> Paid
                            </span>
                          ) : r.paymentOrder?.payment_status === "FAILED" ? (
                            <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                              <FontAwesomeIcon icon={faTimesCircle} /> Failed
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-yellow-600 text-xs font-medium">
                              <FontAwesomeIcon icon={faClock} /> Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {renewalsMeta.totalPages > 1 && (
                <div className="mt-4 flex justify-center items-center gap-1">
                  <PagBtn
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    «
                  </PagBtn>
                  <PagBtn
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </PagBtn>

                  {currentPage > 3 && <span className="px-2 text-gray-400">…</span>}
                  {Array.from({ length: renewalsMeta.totalPages }, (_, i) => i + 1)
                    .filter((p) => p >= currentPage - 2 && p <= currentPage + 2)
                    .map((p) => (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`px-3 py-1 rounded-md text-sm transition ${
                          currentPage === p
                            ? "bg-indigo-600 text-white"
                            : "text-gray-500 hover:bg-indigo-50"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  {currentPage < renewalsMeta.totalPages - 2 && (
                    <span className="px-2 text-gray-400">…</span>
                  )}

                  <PagBtn
                    disabled={currentPage === renewalsMeta.totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </PagBtn>
                  <PagBtn
                    disabled={currentPage === renewalsMeta.totalPages}
                    onClick={() => handlePageChange(renewalsMeta.totalPages)}
                  >
                    »
                  </PagBtn>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-400 text-sm py-6">
              No renewal transactions found.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewSubscriptionDetails;
